
<template>
  <div class="segment-editing-buttons" :class="{ 'is-visible': isVisible }">
    <!-- Delete button -->
    <button
      class="segment-btn delete-btn"
      title="Delete this segment"
      @click="handleDelete"
      :disabled="isDeleting"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 6h18"></path>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
      </svg>
      <span v-if="isDeleting" class="action-text">Deleting...</span>
    </button>

    <!-- Add before button -->
    <button
      class="segment-btn add-btn add-before-btn"
      title="Add segment before this one"
      @click="showAddDialog('before')"
      :disabled="isAdding"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14"></path>
        <path d="M5 12h14"></path>
      </svg>
      <span class="position-indicator">Before</span>
    </button>

    <!-- Add after button -->
    <button
      class="segment-btn add-btn add-after-btn"
      title="Add segment after this one"
      @click="showAddDialog('after')"
      :disabled="isAdding"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14"></path>
        <path d="M5 12h14"></path>
      </svg>
      <span class="position-indicator">After</span>
    </button>

    <!-- Add segment dialog using v-dialog -->
    <v-dialog
      v-model="showDialog"
      max-width="600px"
      persistent
      content-class="segment-dialog"
    >
      <v-card>
        <v-card-title class="dialog-title">
          Add New Segment {{ insertPosition === 'before' ? 'Before' : 'After' }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="newSegment.speaker"
                  :items="availableSpeakers"
                  label="Speaker"
                  placeholder="Select or type new speaker"
                  outlined
                  dense
                  ref="speakerInput"
                >
                  <template v-slot:prepend-item>
                    <v-list-item v-if="newSpeakerName">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-icon left small>mdi-plus</v-icon>
                          Add "{{ newSpeakerName }}"
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newSegment.role"
                  label="Role"
                  placeholder="Speaker role"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="newSegment.section"
                  :items="sectionTypes"
                  item-text="label"
                  item-value="value"
                  label="Section Type"
                  outlined
                  dense
                  return-object
                >
                  <template v-slot:selection="{ item }">
                    <span>{{ item.icon }} {{ item.label }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span>{{ item.icon }} {{ item.label }}</span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="newSegment.content"
                  label="Content"
                  placeholder="Enter segment content"
                  outlined
                  auto-grow
                  rows="5"
                  counter="500"
                  :rules="[v => v.length <= 500 || 'Maximum 500 characters']"
                  ref="contentInput"
                  @keydown.ctrl.enter="addSegment"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            @click="closeDialog"
            :disabled="isSubmitting"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="addSegment"
            :disabled="!newSegment.content || isSubmitting || newSegment.content.length > 500"
            :loading="isSubmitting"
          >
            <span class="black--text">Add Segment</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'SegmentEditingButtons',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    messageId: {
      type: Number,
      required: true
    },
    segmentIndex: {
      type: Number,
      required: true
    },
    currentSpeaker: {
      type: Object,
      default: () => ({})
    },
    currentSection: {
      type: String,
      default: 'custom'
    },
    segments: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isDeleting: false,
      isAdding: false,
      isSubmitting: false,
      showDialog: false,
      insertPosition: 'before',
      customSectionName: '',
      newSpeakerName: '',
      newSegment: {
        speaker: '',
        role: '',
        content: '',
        section: null
      },
      sectionTypes: [
        { value: 'preview', label: 'Preview', icon: '🔍' },
        { value: 'intro', label: 'Intro', icon: '🎙️' },
        { value: 'main', label: 'Main Content', icon: '📝' },
        { value: 'break', label: 'Sponsor Break', icon: '💰' },
        { value: 'last', label: 'Closing', icon: '🏁' },
        { value: 'discussion', label: 'Discussion', icon: '💬' },
        { value: 'insight', label: 'Insight', icon: '💡' },
        { value: 'resonance', label: 'Resonance', icon: '🔄' },
        { value: 'evolution', label: 'Evolution', icon: '🌱' },
        { value: 'portal', label: 'Portal', icon: '🚪' },
        { value: 'custom', label: 'Custom', icon: '🛠️' }
      ]
    };
  },
  computed: {
    availableSpeakers() {
      // Get unique speakers from existing segments
      const speakers = new Set();
      if (this.segments && this.segments.length > 0) {
        this.segments.forEach(segment => {
          if (segment.speaker) {
            speakers.add(segment.speaker);
          }
        });
      }
      return Array.from(speakers).sort();
    },
    contentChars() {
      return this.newSegment.content ? this.newSegment.content.length : 0;
    },
    effectiveSectionType() {
      // Return the section value directly
      return this.newSegment.section ? this.newSegment.section.value : 'custom';
    }
  },
  watch: {
    'newSegment.speaker'(val) {
      // Track new speaker name for the combobox
      if (val && !this.availableSpeakers.includes(val)) {
        this.newSpeakerName = val;
      } else {
        this.newSpeakerName = '';
      }

      // Auto-fill role if we're selecting an existing speaker
      if (val && this.availableSpeakers.includes(val)) {
        // Find the role of this speaker from existing segments
        const existingSegment = this.segments.find(s => s.speaker === val);
        if (existingSegment && existingSegment.role) {
          this.newSegment.role = existingSegment.role;
        }
      }
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
   // In SegmentEditingButtons component
    async handleDelete() {
      if (confirm('Are you sure you want to delete this segment? This cannot be undone.')) {
        this.isDeleting = true;
        try {
          const response = await API.delete(`/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}`);

          if (response.data.success) {
            // Emit as an object instead of multiple parameters to avoild multi-chain issues
            this.$emit('segment-deleted', {
              segmentIndex: this.segmentIndex,
              newSegmentCount: response.data.new_segment_count,
              updatedResponse: response.data.updated_response
            });
          }
        } catch (error) {
          console.error('Error deleting segment:', error);
          this.showTempMessageFn({
            message: 'Failed to delete segment. Please try again.',
            type: 'error',
            timeout: 5000
          });
        } finally {
          this.isDeleting = false;
        }
      }
    },

    showAddDialog(position) {
      this.insertPosition = position;

      // Find the section object that matches the current section
      const currentSectionObj = this.sectionTypes.find(s => s.value === this.currentSection);

      // Pre-fill with current speaker, role, and section
      this.newSegment = {
        speaker: this.currentSpeaker.name || '',
        role: this.currentSpeaker.role || '',
        content: '',
        section: currentSectionObj || this.sectionTypes.find(s => s.value === 'custom')
      };

      this.showDialog = true;

      // Focus the content input after dialog appears
      this.$nextTick(() => {
        if (this.$refs.speakerInput) {
          this.$refs.speakerInput.focus();
        }
      });
    },

    closeDialog() {
      if (!this.isSubmitting) {
        this.showDialog = false;
        this.newSpeakerName = '';
      }
    },

    async addSegment() {
      if (!this.newSegment.content || this.isSubmitting || this.newSegment.content.length > 500) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          position: this.insertPosition,
          content: this.newSegment.content,
          speaker: this.newSegment.speaker || 'Speaker',
          role: this.newSegment.role || 'Role',
          section: this.effectiveSectionType
        };

        const response = await API.post(
          `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/insert`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Emit before closing dialog
          this.$emit('segment-added', {
            position: this.insertPosition,
            index: this.segmentIndex,
            insertedAt: response.data.insert_position,
            segment: response.data.new_segment,
            newSegmentCount: response.data.new_segment_count,
            updatedResponse: response.data.updated_response
          });

          // Reset submitting state before closing
          this.isSubmitting = false;

          // Use nextTick to ensure state is updated
          this.$nextTick(() => {
            this.showDialog = false;
            this.newSpeakerName = '';
          });
        }
      } catch (error) {
        console.error('Error adding segment:', error);
        this.showTempMessageFn({
          message: 'Failed to add segment. Please try again.',
          type: 'error',
          timeout: 5000
        });
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.segment-editing-buttons {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: opacity 0.2s, transform 0.2s;
}

.segment-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: background-color 0.2s, color 0.2s;
}

.segment-btn:hover {
  background-color: #f0f0f0;
  color: #333;
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn svg {
  margin-right: 4px;
}

.delete-btn {
  color: #e53935;
}

.delete-btn:hover {
  background-color: rgba(229, 57, 53, 0.1);
}

.add-btn {
  color: #2196f3;
}

.add-btn:hover {
  background-color: rgba(33, 150, 243, 0.1);
}

.position-indicator {
  font-size: 12px;
  margin-left: 2px;
}

/* Custom dialog styling */
.segment-dialog {
  z-index: 1000 !important;
}

.dialog-title {
  position: relative;
  padding-bottom: 16px;
  font-size: 18px;
  font-weight: 500;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
}

/* Override Vuetify styles if needed */
:deep(.v-text-field__details),
:deep(.v-messages) {
  min-height: 18px !important;
}

:deep(.v-input__slot) {
  margin-bottom: 0 !important;
}
</style>
